import React, { useMemo, useRef, useState } from 'react';

import { RJSFSchema } from '@rjsf/utils';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Chip, Stack } from '@mui/material';

import {
  getRoomRateLabel,
  getSelectedRoomRateLabel,
} from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';

import LinkDealOptionForm from './LinkDealOptionForm';

interface Props {
  dealOption: App.AccommodationPackage;
  packageIndex: number;
  roomRates: Array<App.RoomRate>;
  selectedRoomRateId: string;
  expanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  offer: App.Offer;
  property: App.Property;
  packageSchema: RJSFSchema;
  onPackageSaved: () => void;
  onPackageSaveFailed: (dealOptionLabel: string) => void;
  allSave: number;
}

function getDealOptionOccupancy(dealOption: App.AccommodationPackage) {
  return 'room_occupancy' in dealOption ? dealOption.room_occupancy : undefined;
}

function getDealOptionNumberOfNights(dealOption: App.AccommodationPackage) {
  return 'number_of_nights' in dealOption ? dealOption.number_of_nights : undefined;
}

export default function LinkDealOptionsRow({
  dealOption,
  packageIndex,
  roomRates,
  selectedRoomRateId,
  expanded,
  onExpandedChange,
  onPackageSaved,
  onPackageSaveFailed,
  allSave,
  offer,
  property,
  packageSchema,
}: Props) {
  const numberOfNights = getDealOptionNumberOfNights(dealOption);
  const occupancy = getDealOptionOccupancy(dealOption);
  const roomRate = roomRates.find((rate) => rate.id === selectedRoomRateId);
  const ratePlanLabel = roomRate
    ? getRoomRateLabel(roomRate)
    : getSelectedRoomRateLabel({ offerPackage: dealOption, roomRates, selectedRoomRateId }) || 'Not linked';
  const packageDataWithPendingRate = useMemo(
    () => ({ ...dealOption, fk_room_rate_id: selectedRoomRateId }),
    [dealOption, selectedRoomRateId],
  );

  const seenAllSaveRef = useRef(allSave);
  const isNewAllSaveTrigger = allSave !== seenAllSaveRef.current;

  const [hasBeenExpanded, setHasBeenExpanded] = useState(expanded);
  if (expanded && !hasBeenExpanded) {
    setHasBeenExpanded(true);
  }
  seenAllSaveRef.current = allSave;

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      onChange={(_event, isExpanded) => onExpandedChange(isExpanded)}
      slotProps={{ transition: { timeout: 300 } }}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ pr: 2 }}>
          {numberOfNights ? <Chip size="small" label={`${numberOfNights}N`} sx={{ fontWeight: 700 }} /> : null}
          {occupancy ? <Chip size="small" variant="outlined" label={occupancy} /> : null}
          <Chip size="small" variant="outlined" label={ratePlanLabel} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {(hasBeenExpanded || allSave) && packageSchema && property && (
          <LinkDealOptionForm
            key={`${dealOption.le_package_id ?? dealOption.id_salesforce_external}:${selectedRoomRateId}`}
            packageData={packageDataWithPendingRate}
            packageSchema={packageSchema}
            property={property}
            offer={offer}
            packageIndex={packageIndex}
            allSave={isNewAllSaveTrigger ? allSave : 0}
            onSaveComplete={onPackageSaved}
            onSaveFailed={onPackageSaveFailed}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
